<template>
  <aside class="product-detail__buy mbuy" aria-label="구매 정보">
    <div class="product-detail__buy-card">
      <div class="product-detail__buy-scroll">
        <div class="product-detail__buy-block">
          <div class="product-detail__store-row">
            <p v-if="product.storeName" class="product-detail__store">{{ product.storeName }}</p>
            <div class="product-detail__tools">
              <button type="button" class="product-detail__compare">공유하기</button>
              <button type="button" class="product-detail__compare">찜하기</button>
            </div>
          </div>

          <h1 class="product-detail__title">{{ product.name }}</h1>

          <button type="button" class="product-detail__rating">
            <span class="product-detail__stars" aria-hidden="true">
              <AppIcon
                v-for="star in 5"
                :key="star"
                :icon="star <= Math.round(product.rating) ? 'mdi-star' : 'mdi-star-outline'"
                :size="15"
              />
            </span>
            <strong>{{ product.rating.toFixed(1) }}</strong>
            <em>구매후기 {{ formatPrice(product.reviewCount) }}</em>
          </button>
        </div>

        <div class="product-detail__buy-block">
          <div class="product-detail__price-row">
            <span v-if="isGroupBuy" class="product-detail__amount">{{ formatPrice(cartonPrice) }}<em>원</em><small>/카톤</small></span>
            <span v-else class="product-detail__amount">{{ formatPrice(headlinePrice) }}<em>원</em><small v-if="headlineName">{{ headlineName }}</small><small v-else-if="sellNoun === '박스'">/박스</small></span>
          </div>
          <p v-if="isGroupBuy" class="product-detail__unit">{{ cartonPieces }}개 묶음 · 1개당 {{ formatPrice(priced.unitPrice) }}원</p>
          <p v-else-if="!headlineName" class="product-detail__unit">{{ sellSummary }}<template v-if="product.unit"> · {{ product.unit }}</template></p>
          <p v-else-if="product.unit" class="product-detail__unit">{{ product.unit }}</p>
          <p class="product-detail__stock" :class="{ 'is-out': product.soldOut || stockQty <= 0 }">
            <template v-if="product.soldOut || stockQty <= 0">품절</template>
            <template v-else>재고 {{ formatStockCount(stockQty, product.sellUnit) }}</template>
          </p>
        </div>

        <div class="product-detail__buy-block">
          <dl class="product-detail__buy-facts">
            <div>
              <dt>배송비</dt>
              <dd>{{ productShippingLabel }}</dd>
            </div>
            <div v-if="product.leadTime">
              <dt>출고</dt>
              <dd>{{ product.leadTime }}</dd>
            </div>
            <div>
              <dt>수령</dt>
              <dd>{{ MALL_FULFILLMENT_LIMIT }}</dd>
            </div>
            <div v-if="product.origin">
              <dt>원산지</dt>
              <dd>{{ product.origin }}</dd>
            </div>
            <div v-if="sameDayCutoffText">
              <dt>당일 출고</dt>
              <dd>{{ sameDayCutoffText }}</dd>
            </div>
          </dl>
        </div>

        <div class="product-detail__buy-block">
          <div class="product-detail__options">
            <label v-if="!isGroupBuy" class="product-detail__option-label" :for="selectId">
              옵션 선택 <em>(필수)</em>
            </label>
            <select
              v-if="!isGroupBuy"
              :id="selectId"
              v-model="optionPick"
              class="product-detail__option-select"
              :disabled="product.soldOut || !flatOptionChoices.length"
              @change="onOptionPick"
            >
              <option v-if="!hasDefaultChoice" value="">옵션을 선택하세요</option>
              <option
                v-for="choice in flatOptionChoices"
                :key="choice.name"
                :value="choice.name"
                :disabled="!isChoiceAvailable(choice)"
              >
                {{ choiceLabel(choice) }}
              </option>
            </select>

            <MallOrderPackMark
              v-if="isPack && !isGroupBuy"
              size="detail"
              :order-step="orderStep"
              :sell-unit="product.sellUnit"
            />

            <ul v-if="selectedLines.length" class="product-detail__picks">
              <li v-for="line in selectedLines" :key="line.key">
                <div class="product-detail__pick-meta">
                  <strong>{{ isGroupBuy ? '1카톤' : line.optionName }}</strong>
                  <span v-if="isGroupBuy">{{ formatPrice(lineUnitPrice(line.optionExtra) * cartonPieces) }}원 × {{ cartonCount(line.qty) }}</span>
                  <span v-else>{{ formatPrice(lineUnitPrice(line.optionExtra)) }}원 × {{ line.qty }}</span>
                </div>
                <div class="product-detail__pick-ops">
                  <QtyStepper
                    :model-value="line.qty"
                    :min="minQty"
                    :max="maxQty"
                    :step="orderStep"
                    :unit="sellNoun"
                    :carton="isGroupBuy"
                    @update:model-value="setLineQty(line.key, $event)"
                  />
                </div>
              </li>
            </ul>

            <p v-if="qtyHint" class="product-detail__qty-hint">{{ qtyHint }}</p>
          </div>
        </div>
      </div>

      <div class="product-detail__buy-block product-detail__buy-block--foot">
        <div class="product-detail__total">
          <div class="product-detail__total-copy">
            <span>주문금액</span>
            <em v-if="orderShippingFee > 0">주문 배송비 {{ formatPrice(orderShippingFee) }}원 포함</em>
            <em v-else>주문 배송비 무료</em>
          </div>
          <strong>{{ formatPrice(totalAmount) }}<em>원</em></strong>
        </div>

        <div class="product-detail__actions">
          <button
            v-if="product.soldOut"
            type="button"
            class="product-detail__cta product-detail__cta--restock"
          >
            재입고 알림 신청
          </button>
          <button v-else type="button" class="product-detail__cta product-detail__cta--buy">
            바로구매
          </button>
          <div class="product-detail__action-row">
            <button type="button" class="product-detail__tool">
              <AppIcon icon="mdi-heart-outline" :size="20" />
              찜하기
            </button>
            <button type="button" class="product-detail__tool" :disabled="product.soldOut">
              <AppIcon icon="mdi-cart-outline" :size="20" />
              장바구니
            </button>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import MallOrderPackMark from '@/components/mall/MallOrderPackMark.vue'
import QtyStepper from '@/components/mall/QtyStepper.vue'
import { MALL_FULFILLMENT_LIMIT } from '@/constants/mall'
import { formatPrice, type MallProduct, type MallProductOption } from '@/constants/mallProducts'
import { useSameDayCutoffText } from '@/composables/useContentSnippets'
import { calcMallUnitPrice } from '@/utils/mallPricing'
import { toShippingLine } from '@/utils/mallShipping'
import {
  calcOrderShippingFromLines,
  formatMinOrderLabel,
  formatShippingChargeLabel,
  formatStockCount,
  isOrderPack,
  optionPieceCount,
  sellUnitNoun,
  sellUnitSummary,
  snapOrderQty,
  storefrontOffer,
} from '@/utils/mallSell'

let selectSeq = 0

const props = defineProps<{
  product: MallProduct
}>()

const selectId = `mbuy-option-${++selectSeq}`
const optionPick = ref('')

type SelectedLine = {
  key: string
  optionName: string
  optionExtra: number
  qty: number
}

const selectedLines = ref<SelectedLine[]>([])

const { sameDayCutoffText, loadSameDayCutoffSnippet } = useSameDayCutoffText(
  () => props.product.storeCutoff,
)

onMounted(() => {
  void loadSameDayCutoffSnippet()
})

const productOptions = computed(() =>
  (props.product.options ?? []).filter((option) => option.use !== false && option.name.trim()),
)

const optionGroups = computed(() => {
  const groups = new Map<string, MallProductOption[]>()
  productOptions.value.forEach((option) => {
    const groupName = option.group?.trim() || '구성'
    const list = groups.get(groupName) ?? []
    list.push(option)
    groups.set(groupName, list)
  })
  return [...groups.entries()].map(([name, items]) => ({ name, items }))
})

function choiceMoneyLabel(name: string, extra: number) {
  const total = Math.max(0, (Number(props.product.price) || 0) + (Number(extra) || 0))
  const qty = optionPieceCount(name)
  const totalText = `${formatPrice(total)}원`
  if (qty === 1) return `${name} ${totalText}`
  if (qty && qty > 1) return `${name} ${totalText} · 개당 ${formatPrice(Math.round(total / qty))}원`
  if (extra) return `${name} (${extra > 0 ? '+' : ''}${formatPrice(extra)}원)`
  return name
}

const flatOptionChoices = computed(() => {
  const groups = optionGroups.value
  if (!groups.length) return [] as Array<{ name: string; label: string; extra: number; stock: number; front: boolean }>

  const flatItems = groups.flatMap((group) => group.items)
  const quantityList =
    flatItems.length > 0 && flatItems.every((option) => optionPieceCount(option.name) != null)
  if (groups.length === 1 || quantityList) {
    return (quantityList ? flatItems : groups[0].items)
      .map((option) => ({
        name: option.name,
        label: choiceMoneyLabel(option.name, option.extra),
        extra: option.extra,
        stock: option.stock,
        front: option.front === true,
      }))
      .sort((a, b) => (optionPieceCount(a.name) ?? 9999) - (optionPieceCount(b.name) ?? 9999))
  }

  const [first, ...rest] = groups
  let combos = first.items.map((option) => ({
    parts: [option.name],
    extra: option.extra,
    stock: option.stock,
  }))
  rest.forEach((group) => {
    const next: typeof combos = []
    combos.forEach((combo) => {
      group.items.forEach((option) => {
        next.push({
          parts: [...combo.parts, option.name],
          extra: combo.extra + option.extra,
          stock: Math.min(combo.stock, option.stock),
        })
      })
    })
    combos = next
  })

  return combos.map((combo) => {
    const name = combo.parts.join(' / ')
    return {
      name,
      label: choiceMoneyLabel(name, combo.extra),
      extra: combo.extra,
      stock: combo.stock,
      front: false,
    }
  })
})

const hasDefaultChoice = computed(() =>
  flatOptionChoices.value.some(
    (item) => item.front || optionPieceCount(item.name) === 1 || item.extra === 0,
  ),
)

const tracksOptionStock = computed(() => {
  const choices = flatOptionChoices.value
  if (choices.length && choices.every((item) => optionPieceCount(item.name) != null)) return false
  return choices.some((item) => item.stock > 0)
})

function isChoiceAvailable(choice: { stock: number }) {
  if (props.product.soldOut) return false
  if (!tracksOptionStock.value) return true
  return choice.stock > 0
}

function choiceLabel(choice: { name: string; label: string; stock: number }) {
  return isChoiceAvailable(choice) ? choice.label : `${choice.label} · 품절`
}

const sellNoun = computed(() => sellUnitNoun(props.product.sellUnit))
const sellSummary = computed(() => sellUnitSummary(props.product))
const productShippingLabel = computed(() => {
  const label = formatShippingChargeLabel(props.product)
  return label === '무료' ? '무료배송' : label
})
const minQty = computed(() => Math.max(1, props.product.minOrder || 1))
const orderStep = computed(() => Math.max(1, props.product.orderStep || 1))
const isPack = computed(() => isOrderPack(orderStep.value))
const isGroupBuy = computed(() => props.product.mallKind === 'SALE')
const cartonPieces = computed(() => Math.max(1, props.product.packQty || orderStep.value || 1))
const stockQty = computed(() => Math.max(0, Number(props.product.stock) || 0))
const maxQty = computed(() => {
  const stock = stockQty.value
  return Math.max(minQty.value, stock > 0 ? stock : minQty.value)
})
const qtyHint = computed(() => {
  if (props.product.soldOut || stockQty.value <= 0) return '품절'
  if (isPack.value) return ''
  if (minQty.value > 1) return formatMinOrderLabel(minQty.value, props.product.sellUnit)
  return ''
})

function clampQty(value: number) {
  return snapOrderQty(value, minQty.value, orderStep.value, maxQty.value)
}

function cartonCount(qty: number) {
  return Math.max(1, Math.round((Number(qty) || cartonPieces.value) / cartonPieces.value))
}

const priced = computed(() =>
  calcMallUnitPrice({
    listPrice: props.product.listPrice,
    salePrice: props.product.price,
  }),
)

const cartonPrice = computed(() => priced.value.unitPrice * cartonPieces.value)

function lineUnitPrice(optionExtra = 0): number {
  return calcMallUnitPrice({
    listPrice: props.product.listPrice,
    salePrice: props.product.price,
    optionExtra,
  }).unitPrice
}

function seedDefaultLine() {
  selectedLines.value = []
  optionPick.value = ''
  const choices = flatOptionChoices.value
  const choice =
    choices.find((item) => item.front && isChoiceAvailable(item)) ??
    choices.find((item) => item.front) ??
    choices.find((item) => optionPieceCount(item.name) === 1 && isChoiceAvailable(item)) ??
    choices.find((item) => optionPieceCount(item.name) === 1) ??
    choices.find((item) => item.extra === 0 && isChoiceAvailable(item)) ??
    choices.find((item) => item.extra === 0)
  if (!choice) return
  optionPick.value = choice.name
  if (!isChoiceAvailable(choice)) return
  selectedLines.value = [
    {
      key: `${props.product.id}::${choice.name}`,
      optionName: choice.name,
      optionExtra: choice.extra,
      qty: clampQty(minQty.value),
    },
  ]
}

watch(
  () => {
    const options = productOptions.value
      .map((option) => `${option.group}:${option.name}:${option.extra}:${option.stock}:${option.front}`)
      .join('|')
    return `${props.product.soldOut ? 1 : 0}|${options}`
  },
  () => {
    seedDefaultLine()
  },
  { immediate: true },
)

watch(maxQty, () => {
  selectedLines.value.forEach((line) => {
    line.qty = clampQty(line.qty)
  })
})

function onOptionPick() {
  const name = optionPick.value
  if (!name) return
  const choice = flatOptionChoices.value.find((item) => item.name === name)
  if (!choice || !isChoiceAvailable(choice)) {
    optionPick.value =
      selectedLines.value[0]?.optionName ||
      flatOptionChoices.value.find((item) => isChoiceAvailable(item))?.name ||
      ''
    return
  }
  if (selectedLines.value[0]?.optionName === name) return
  selectedLines.value = [
    {
      key: `${props.product.id}::${name}`,
      optionName: name,
      optionExtra: choice.extra,
      qty: clampQty(1),
    },
  ]
}

function setLineQty(key: string, raw: string | number) {
  const line = selectedLines.value.find((item) => item.key === key)
  if (!line) return
  const parsed = typeof raw === 'number' ? raw : Number(String(raw).trim())
  line.qty = clampQty(Number.isFinite(parsed) ? Math.trunc(parsed) : minQty.value)
}

const goodsAmount = computed(() => {
  if (selectedLines.value.length) {
    return selectedLines.value.reduce((sum, line) => sum + lineUnitPrice(line.optionExtra) * line.qty, 0)
  }
  return priced.value.unitPrice * minQty.value
})

const pickQty = computed(() => {
  if (selectedLines.value.length) {
    return selectedLines.value.reduce((sum, line) => sum + line.qty, 0)
  }
  return minQty.value
})

const orderShippingFee = computed(() =>
  calcOrderShippingFromLines([toShippingLine(props.product, pickQty.value)]),
)

const totalAmount = computed(() => goodsAmount.value + orderShippingFee.value)

const headlineOffer = computed(() => storefrontOffer(props.product))
const headlinePrice = computed(() => {
  const line = selectedLines.value[0]
  if (line && props.product.mallKind !== 'SALE') return lineUnitPrice(line.optionExtra)
  return headlineOffer.value.price || priced.value.unitPrice
})
const headlineName = computed(() => {
  if (props.product.mallKind === 'SALE') return ''
  return selectedLines.value[0]?.optionName || headlineOffer.value.name
})
</script>

<style lang="scss" src="../../pages/mall/MallProductPage.scss"></style>
<style lang="scss" src="./MallProductBuyPanel.scss"></style>
